export const CASES = {
  helios: {
    slug: "helios",
    client: "Helios Labs",
    title: "Realtime trading intelligence",
    tag: "AI · Web",
    year: "2025",
    duration: "14 weeks",
    role: "Design · Engineering · ML Ops",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9kZSUyMGRhc2hib2FyZCUyMHVpfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
    summary:
      "A sub‑200ms trading copilot that ingests order books, news and sentiment to surface high‑signal opportunities for a mid‑market hedge fund.",
    problem:
      "Helios analysts were drowning in 6 separate dashboards and lost an average of 90 seconds per decision. Latency was killing alpha.",
    approach: [
      "Replaced the multi-dashboard sprawl with a single, keyboard-first command surface.",
      "Built a streaming feature store on top of ClickHouse + Redis with sub-50ms p95.",
      "Trained a domain-tuned LLM judge to rank news/sentiment events against open positions.",
      "Wrapped every model decision with a full eval harness and human-in-the-loop overrides.",
    ],
    outcome: [
      { label: "Decision latency", value: "−72%" },
      { label: "Alpha (90d)", value: "+18%" },
      { label: "Eval coverage", value: "97%" },
    ],
    stack: ["Next.js", "React", "Rust", "ClickHouse", "Redis", "OpenAI", "Vercel"],
  },
  sentinel: {
    slug: "sentinel",
    client: "Sentinel",
    title: "Zero‑trust security mesh",
    tag: "Cybersec",
    year: "2024",
    duration: "9 weeks",
    role: "Threat modelling · Backend · DevSecOps",
    image:
      "https://images.unsplash.com/photo-1751448555253-f39c06e29d82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjeWJlciUyMHNlY3VyaXR5JTIwYWJzdHJhY3QlMjBkYXJrfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
    summary:
      "Replaced a flat corporate VPN with an identity‑aware zero‑trust mesh, hardening 2,400 endpoints across 14 countries.",
    problem:
      "A single compromised contractor laptop could reach production. Network was flat, audit trails were partial, MFA was inconsistent.",
    approach: [
      "Mapped every trust boundary and produced a STRIDE-based threat model.",
      "Rolled out identity-aware proxies and SPIFFE workload identities.",
      "Closed 11 critical CVEs and built an automated red-team replay harness.",
      "Wrote runbooks and ran two live tabletop incident exercises with the SOC.",
    ],
    outcome: [
      { label: "Critical CVEs closed", value: "11" },
      { label: "MTTR (incidents)", value: "−61%" },
      { label: "Audit findings", value: "0" },
    ],
    stack: ["Go", "Envoy", "SPIFFE", "Tailscale", "AWS", "Terraform"],
  },
  atlas: {
    slug: "atlas",
    client: "Atlas Capital",
    title: "Autonomous research agents",
    tag: "AI Agents",
    year: "2025",
    duration: "6 weeks",
    role: "Agent design · Eval · Frontend",
    image:
      "https://images.unsplash.com/photo-1709625862266-014ef072fd93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJ5JTIwZGFyayUyMDNkfGVufDB8fHx8MTc4MTM2ODEzM3ww&ixlib=rb-4.1.0&q=85",
    summary:
      "A fleet of investment‑research agents that read filings, run financial models and draft memos — supervised, not autonomous.",
    problem:
      "Junior analysts were spending 70% of their week on bookkeeping work — reading 10‑Ks, building DCFs, formatting memos.",
    approach: [
      "Decomposed analyst work into 9 deterministic + LLM-graded sub-tasks.",
      "Built a tool-using agent with strict schemas, caching and replayable traces.",
      "Designed a memo authoring surface that lets analysts edit any agent step.",
      "Hardened with an LLM-judge eval suite over 1,200 historical memos.",
    ],
    outcome: [
      { label: "Memos / analyst / week", value: "3.2× more" },
      { label: "Time on bookkeeping", value: "−68%" },
      { label: "Hallucination rate", value: "<0.4%" },
    ],
    stack: ["LangGraph", "Python", "Postgres", "React", "OpenAI", "Anthropic"],
  },
  lumen: {
    slug: "lumen",
    client: "Lumen Health",
    title: "Cross‑platform clinician app",
    tag: "Mobile · Desktop",
    year: "2024",
    duration: "18 weeks",
    role: "Product design · Mobile · Desktop · Backend",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9kZSUyMGRhc2hib2FyZCUyMHVpfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
    summary:
      "One product surface across iOS, Android and macOS for emergency clinicians — designed for one‑handed use under pressure.",
    problem:
      "Clinicians were juggling three apps and a clipboard. Charting was slow and error‑prone during triage.",
    approach: [
      "Field-studied 22 clinicians across three EDs and rewrote the IA around triage.",
      "Built a React Native + macOS Catalyst shell sharing a single Rust core.",
      "Added on-device voice charting with whisper-grade transcription.",
      "Integrated with three EHRs via HL7 FHIR with full audit trails.",
    ],
    outcome: [
      { label: "Time to chart", value: "−54%" },
      { label: "App switches / shift", value: "−81%" },
      { label: "Clinician NPS", value: "+62" },
    ],
    stack: ["React Native", "Swift", "Rust", "FHIR", "Postgres", "Whisper"],
  },
};

export const CASE_LIST = ["helios", "sentinel", "atlas", "lumen"].map(
  (k) => CASES[k],
);
